/**
 * ONE TAP — procedural convolution reverb.
 *
 * No impulse response files. Every space in the game is a Float32Array we synthesise at
 * startup: sparse early reflections in front of a band-split exponentially decaying noise
 * tail, with the high band decaying faster than the low band the way real air and concrete
 * behave.
 *
 * Three spaces:
 *  - `atrium`   the arena itself. Big, hard, concrete, long. This is where the gunshot lives —
 *               the tail is the reason a shot reads as an EVENT rather than a click.
 *  - `interior` tighter rooms, catwalks, under overhangs. Short and defined; used for the
 *               mechanical sounds so they stay crisp but not anechoic.
 *  - `plate`    a bright synthetic plate for music only. Wider and denser than either room.
 */

import { clamp, clamp01, lerpN, makeRng } from '@shared/math';
import { gain } from './synth';

export type IRKind = 'atrium' | 'interior' | 'plate';

interface IRSpec {
  /** Total impulse length, seconds. */
  seconds: number;
  /** Silence before the first reflection, seconds. */
  preDelay: number;
  /** RT60 of the low band, seconds. */
  rt60Low: number;
  /** RT60 of the high band, seconds — always shorter; air and concrete eat treble. */
  rt60High: number;
  /** Crossover between the two bands, Hz. */
  crossover: number;
  /** Number of discrete early reflections. */
  earlyCount: number;
  /** Time span the early reflections are spread over, seconds. */
  earlySpread: number;
  /** How long the diffuse tail takes to build up, seconds. Big rooms bloom. */
  buildup: number;
  /** Stereo decorrelation, 0..1. */
  width: number;
  /** One-pole highpass on the tail, Hz — keeps the sends out of the sub range. */
  highpass: number;
  /** Final peak scaling. */
  level: number;
}

const SPECS: Record<IRKind, IRSpec> = {
  atrium: {
    // A longer pre-delay than a "correct" room would have. It is deliberate: the gap between
    // the dry attack and the first reflection is what lets a gunshot read as bone dry AND
    // enormous at the same time. Shorten it and the shot goes hollow.
    // The tail got longer again. DESIGN.md §5 asks the gunshot for "a long tail down the
    // atrium", and a 3.4 s RT60 in a concrete arena this size is conservative — real ones
    // run past 4. It costs nothing at the attack because the send is gated shut across the
    // first 26 ms regardless, so all this does is give the report somewhere to go after the
    // crack has already landed dry.
    seconds: 4.4,
    preDelay: 0.031,
    rt60Low: 4.1,
    rt60High: 1.3,
    crossover: 1400,
    earlyCount: 22,
    earlySpread: 0.15,
    buildup: 0.055,
    width: 0.9,
    highpass: 120,
    // Only a hair above the old 0.62, and deliberately so. The per-voice sends were opened
    // up because they are now gated shut across the transient — raising the impulse level
    // as well would be double-dipping on wetness, and "too wet" is the primary cause of the
    // hollowness this whole pass exists to fix. The tail got LONGER, not louder.
    level: 0.66,
  },
  interior: {
    seconds: 0.95,
    preDelay: 0.011,
    rt60Low: 0.78,
    rt60High: 0.31,
    crossover: 1900,
    earlyCount: 11,
    earlySpread: 0.032,
    buildup: 0.008,
    width: 0.6,
    highpass: 180,
    level: 0.54,
  },
  plate: {
    seconds: 2.0,
    preDelay: 0.0,
    rt60Low: 1.75,
    rt60High: 1.15,
    crossover: 900,
    earlyCount: 6,
    earlySpread: 0.02,
    buildup: 0.012,
    width: 1.0,
    highpass: 240,
    level: 0.55,
  },
};

/** One-pole coefficient for a given cutoff at a given sample rate. */
function onePole(cutoffHz: number, sampleRate: number): number {
  return clamp01(1 - Math.exp((-2 * Math.PI * cutoffHz) / sampleRate));
}

/**
 * Synthesise a stereo impulse response.
 *
 * The two channels use independent noise streams (decorrelated) and slightly different early
 * reflection times, which is what makes the tail feel wide instead of like a mono blob glued
 * to the centre of the head.
 */
export function generateImpulse(ctx: BaseAudioContext, kind: IRKind, seed = 0x07e7a9): AudioBuffer {
  const spec = SPECS[kind];
  const sr = ctx.sampleRate;
  const n = Math.max(256, Math.round(spec.seconds * sr));
  const buf = ctx.createBuffer(2, n, sr);

  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    const rng = makeRng((seed + ch * 7919 + kind.length * 104729) >>> 0);

    const preDelaySamples = Math.round(spec.preDelay * sr);
    const buildSamples = Math.max(1, Math.round(spec.buildup * sr));

    // Band split state.
    let lowState = 0;
    let hpState = 0;
    let hpPrev = 0;
    const hpCoef = Math.exp((-2 * Math.PI * spec.highpass) / sr);

    // The crossover slowly closes over the tail so late reverb is darker than early reverb.
    const coefStart = onePole(spec.crossover, sr);
    const coefEnd = onePole(spec.crossover * 0.35, sr);

    const decayLow = Math.log(1000) / Math.max(spec.rt60Low, 0.05);
    const decayHigh = Math.log(1000) / Math.max(spec.rt60High, 0.05);

    for (let i = 0; i < n; i++) {
      if (i < preDelaySamples) {
        d[i] = 0;
        continue;
      }
      const rel = i - preDelaySamples;
      const t = rel / sr;

      const w = rng() * 2 - 1;
      const coef = lerpN(coefStart, coefEnd, clamp01(t / spec.seconds));
      lowState += (w - lowState) * coef;
      const high = w - lowState;

      const envLow = Math.exp(-decayLow * t);
      const envHigh = Math.exp(-decayHigh * t);
      // Diffuse buildup: a big concrete room does not reach full density instantly.
      const build = rel >= buildSamples ? 1 : rel / buildSamples;

      let s = (lowState * envLow + high * envHigh) * build;

      // One-pole highpass so the sends never muddy the low end where the sub layer lives.
      hpState = hpCoef * (hpState + s - hpPrev);
      hpPrev = s;
      s = hpState;

      d[i] = s;
    }

    // Early reflections: discrete, loud, and the thing that actually tells you the room's size.
    for (let k = 0; k < spec.earlyCount; k++) {
      const frac = (k + rng() * 0.8) / spec.earlyCount;
      const tap = preDelaySamples + Math.round((0.002 + frac * spec.earlySpread) * sr * (1 + (ch ? 0.11 : -0.07) * spec.width));
      if (tap <= 0 || tap >= n - 2) continue;
      const amp = (1 - frac) * (1 - frac) * 0.85 * (rng() < 0.5 ? -1 : 1) * (0.6 + 0.4 * rng());
      d[tap] += amp;
      d[tap + 1] += amp * 0.45;
      d[tap + 2] += amp * 0.16;
    }

    // Head and tail shaping so the convolution has no discontinuity at either end.
    const headFade = Math.max(2, Math.round(0.0008 * sr));
    for (let i = 0; i < headFade && i < n; i++) d[i] *= i / headFade;
    const tailFade = Math.max(64, Math.round(n * 0.12));
    for (let i = n - tailFade; i < n; i++) {
      const f = (n - i) / tailFade;
      d[i] *= f * f;
    }
  }

  // Normalise both channels together so the stereo image is preserved.
  let peak = 0;
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < n; i++) {
      const a = Math.abs(d[i]);
      if (a > peak) peak = a;
    }
  }
  if (peak > 1e-6) {
    const s = spec.level / peak;
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < n; i++) d[i] *= s;
    }
  }

  return buf;
}

interface ReverbSlot {
  send: GainNode;
  pre: BiquadFilterNode;
  convolver: ConvolverNode;
  ret: GainNode;
  buffer: AudioBuffer;
}

/**
 * The reverb sends. Sounds connect into `send(kind)`; the wet return lands on the node this
 * bank was constructed with.
 *
 * Sends are pre-panner by design. Reverb in a room is diffuse — it should not swing across
 * the stereo field with the source. Distance-dependent wet amount is applied by the engine
 * when it builds each voice, which is what actually sells depth.
 */
export class ReverbBank {
  private readonly slots = new Map<IRKind, ReverbSlot>();
  private disposed = false;

  constructor(
    private readonly ctx: BaseAudioContext,
    destination: AudioNode,
    kinds: IRKind[] = ['atrium', 'interior'],
    seed = 0x07e7a9,
  ) {
    for (const kind of kinds) {
      const buffer = generateImpulse(ctx, kind, seed);

      const send = gain(ctx, 1);
      // Roll off the very top of the send: a hall does not reflect 18 kHz, and this keeps the
      // reverb from adding harshness over a 90 second round.
      const pre = ctx.createBiquadFilter();
      pre.type = 'lowpass';
      pre.frequency.value = kind === 'atrium' ? 7200 : 9000;
      pre.Q.value = 0.5;

      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = kind === 'atrium' ? 150 : 220;
      hp.Q.value = 0.7;

      const convolver = ctx.createConvolver();
      convolver.normalize = false;
      convolver.buffer = buffer;

      const ret = gain(ctx, kind === 'atrium' ? 0.9 : 0.75);

      send.connect(pre);
      pre.connect(hp);
      hp.connect(convolver);
      convolver.connect(ret);
      ret.connect(destination);

      this.slots.set(kind, { send, pre, convolver, ret, buffer });
    }
  }

  /** Input node for a given space. Connect a sound here to place it in that room. */
  send(kind: IRKind): AudioNode {
    const slot = this.slots.get(kind);
    if (slot) return slot.send;
    // Fall back to whatever exists rather than throwing mid-firefight.
    const first = this.slots.values().next().value as ReverbSlot | undefined;
    if (first) return first.send;
    return gain(this.ctx, 0);
  }

  /** Raw impulse buffer, so other subsystems (the music bus) can build their own convolver. */
  buffer(kind: IRKind): AudioBuffer | null {
    return this.slots.get(kind)?.buffer ?? null;
  }

  /** Wet return level for a space. Used to open the atrium up during HIGH NOON. */
  setReturn(kind: IRKind, value: number, rampSec = 0.2): void {
    const slot = this.slots.get(kind);
    if (!slot) return;
    const t = this.ctx.currentTime;
    slot.ret.gain.cancelScheduledValues(t);
    slot.ret.gain.setValueAtTime(slot.ret.gain.value, t);
    slot.ret.gain.linearRampToValueAtTime(clamp(value, 0, 4), t + Math.max(rampSec, 0.01));
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const slot of this.slots.values()) {
      try {
        slot.send.disconnect();
        slot.pre.disconnect();
        slot.convolver.disconnect();
        slot.ret.disconnect();
      } catch {
        /* graph already torn down */
      }
      slot.convolver.buffer = null;
    }
    this.slots.clear();
  }
}
